import { getUrls } from "utils/urls";
import useApiWrapper from "./useApiWrapper";

const useWSMApi = () => {
  const { fetchWithAuth } = useApiWrapper();
  const URLS = getUrls();

  const registerUser = async (data: any) => {
    return await fetchWithAuth(URLS.REGISTER_USER, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  };

  const sendLoginOTP = async (data: any) => {
    return await fetchWithAuth(URLS.SEND_OTP, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  };

  const validateLoginOTP = async (data: any) => {
    return await fetchWithAuth(URLS.VALIDATE_OTP, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  };

  const fetchUserProfile = async () => {
    return await fetchWithAuth(URLS.GET_USER_PROFILE, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const updateUserProfile = async (data: any) => {
    return await fetchWithAuth(URLS.UPDATE_USER_PROFILE, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  };

  const updateProfilePic = async (data: any) => {
    return await fetchWithAuth(URLS.UPDATE_PROFILE_PIC, {
      method: "POST",
      body: data,
    });
  };

  const fetchS3File = async (key: string) => {
    const encodedKey = encodeURIComponent(key);
    return await fetchWithAuth(URLS.GET_S3_FILE(encodedKey), {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchWSMSlots = async (data: any) => {
    const queryParams = new URLSearchParams(data).toString();
    return await fetchWithAuth(`${URLS.GET_WSM_SLOTS}?${queryParams}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchNextAvailableDate = async (data: {
    date: string;
    type: string;
  }): Promise<{ date: string | null }> => {
    const queryParams = new URLSearchParams(data).toString();
    const response = await fetchWithAuth(
      `${URLS.GET_NEXT_AVAILABLE_SLOT}?${queryParams}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      },
    );
    return response;
  };

  const fetchWSMSlotById = async (slotId: string) => {
    return await fetchWithAuth(URLS.GET_WSM_SLOT(slotId), {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchExclusiveSlot = async () => {
    return await fetchWithAuth(URLS.GET_EXCLUSIVE_SLOT, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchBulletinSlot = async () => {
    return await fetchWithAuth(URLS.GET_BULLETIN_SLOT, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchUserCredits = async () => {
    return await fetchWithAuth(URLS.GET_USER_CREDITS, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const reserveWSMSlot = async (slotData: any) => {
    return await fetchWithAuth(URLS.RESERVE_WSM_SLOT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(slotData),
    });
  };

  const updateReservation = async (data: any) => {
    return await fetchWithAuth(URLS.UPDATE_RESERVATION, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  };

  const cancelReservation = async (slotData: any) => {
    return await fetchWithAuth(URLS.CANCEL_RESERVATION, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(slotData),
    });
  };

  const fetchReservations = async () => {
    return await fetchWithAuth(URLS.GET_RESERVATIONS, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchAllReservations = async () => {
    return await fetchWithAuth(URLS.GET_ALL_RESERVATIONS, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchReservationBySlotId = async (slotId: string) => {
    return await fetchWithAuth(URLS.GET_SLOT_RESERVATION(slotId), {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchComingUpEvents = async () => {
    return await fetchWithAuth(URLS.GET_COMING_UP_SLOTS, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchMembershipList = async () => {
    return await fetchWithAuth(URLS.GET_MEMBERSHIP_LIST, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const createPayment = async (data: any) => {
    return await fetchWithAuth(URLS.CREATE_PAYMENT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  };

  const verifyPayment = async (data: any) => {
    return await fetchWithAuth(URLS.VERIFY_PAYMENT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  };

  const fetchCreditPlans = async () => {
    return await fetchWithAuth(URLS.GET_CREDIT_PLANS, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchPassesList = async () => {
    return await fetchWithAuth(URLS.GET_PASSES_LIST, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchPaymentHistory = async (data: any) => {
    const queryParams = new URLSearchParams(data).toString();
    return await fetchWithAuth(`${URLS.GET_PAYMENT_HISTORY}?${queryParams}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchScoreCard = async (data: any) => {
    const queryParams = new URLSearchParams(data).toString();
    return await fetchWithAuth(`${URLS.GET_SCORE_CARD}?${queryParams}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchUserScoreCard = async () => {
    return await fetchWithAuth(URLS.GET_USER_SCORE_CARD, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchUserScoreCardResult = async () => {
    return await fetchWithAuth(URLS.GET_USER_SCORE_CARD_RESULT, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const addUserScoreCard = async (data: any) => {
    return await fetchWithAuth(URLS.ADD_USER_SCORE_CARD, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  };

  const fetchCustomerLead = async (token: string) => {
    return await fetchWithAuth(URLS.GET_CUSTOMER_LEAD(token), {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchPasses = async (data: any) => {
    const queryParams = new URLSearchParams(data).toString();
    return await fetchWithAuth(`${URLS.GET_PASSES}?${queryParams}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchMembershipByType = async (data: any) => {
    const queryParams = new URLSearchParams(data).toString();
    return await fetchWithAuth(
      `${URLS.GET_MEMBERSHIP_BY_TYPE}?${queryParams}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      },
    );
  };

  const fetchUpcomingPasses = async (data: any) => {
    const queryParams = new URLSearchParams(data).toString();
    return await fetchWithAuth(`${URLS.GET_UPCOMING_PASSES}?${queryParams}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchExperiencePasses = async (data: any) => {
    const queryParams = new URLSearchParams(data).toString();
    return await fetchWithAuth(`${URLS.GET_EXPERIENCE_PASSES}?${queryParams}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const sendEventReserveLead = async (data: any) => {
    return await fetchWithAuth(URLS.SEND_EVENT_RESERVE_LEAD, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  };

  const fetchEventOffers = async (data: any) => {
    return await fetchWithAuth(URLS.GET_EVENT_OFFERS, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  };

  const fetchEventPassQuestions = async (data: any) => {
    const queryParams = new URLSearchParams(data).toString();
    return await fetchWithAuth(`${URLS.GET_EVENT_PASS_QUESTION}?${queryParams}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  const fetchProduct = async (type: string) => {
    return await fetchWithAuth(URLS.GET_PRODUCT(type), {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
  };

  return {
    registerUser,
    sendLoginOTP,
    validateLoginOTP,
    fetchUserProfile,
    updateUserProfile,
    updateProfilePic,
    fetchS3File,
    fetchWSMSlots,
    fetchNextAvailableDate,
    fetchWSMSlotById,
    fetchExclusiveSlot,
    fetchBulletinSlot,
    fetchUserCredits,
    reserveWSMSlot,
    updateReservation,
    cancelReservation,
    fetchReservations,
    fetchAllReservations,
    fetchReservationBySlotId,
    fetchComingUpEvents,
    fetchMembershipList,
    createPayment,
    verifyPayment,
    fetchCreditPlans,
    fetchPassesList,
    fetchPaymentHistory,
    fetchScoreCard,
    fetchUserScoreCard,
    fetchUserScoreCardResult,
    addUserScoreCard,
    fetchCustomerLead,
    fetchPasses,
    fetchMembershipByType,
    fetchUpcomingPasses,
    fetchExperiencePasses,
    sendEventReserveLead,
    fetchEventOffers,
    fetchEventPassQuestions,
    fetchProduct,
  };
};

export default useWSMApi;
